import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useDeferredAction — Phase 4.0b receptionist undo-cancel pattern.
 *
 * Schedules a destructive action (cancel sitting, etc.) behind a
 * countdown. The action only fires when the timer expires; calling
 * `undo(id)` before then drops it without ever hitting the backend.
 *
 * UX contract:
 *   - schedule(fire, { delayMs, label, key? }) → returns id
 *     `fire` is `() => Promise<any>`. Re-fired exactly once on timeout.
 *   - pending: live array of { id, label, expiresAt } so the caller can
 *     render undo toasts. Updates every 250ms while at least one item
 *     is in flight (no interval when the queue is empty).
 *   - undo(id): drops the item from the queue and clears its timer.
 *   - flushAll() / fireAll(): for cleanup; not used today.
 *   - On unmount: every queued action is cancelled (NOT fired). This
 *     matches the user's locked decision — closing the tab during the
 *     5s window means the cancel never reaches the backend.
 */
export default function useDeferredAction({ tickMs = 250 } = {}) {
  const [pending, setPending] = useState([]);   // [{id, label, expiresAt}]
  const timersRef = useRef(new Map());          // id → { handle, key }
  const tickRef = useRef(null);
  const idCounterRef = useRef(0);

  // Tick re-renders pending entries so the toast countdown updates
  // smoothly. Self-cancels when the queue empties.
  useEffect(() => {
    if (pending.length === 0) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return undefined;
    }
    if (!tickRef.current) {
      tickRef.current = setInterval(() => {
        setPending((prev) => {
          // Drop expired entries here as a backstop — the timer
          // callback should already have removed them, but a stale
          // entry can sneak in if the browser was throttled.
          const now = Date.now();
          const next = prev.filter((p) => p.expiresAt > now - 50);
          return next.length === prev.length ? prev : next;
        });
      }, tickMs);
    }
    return undefined;
  }, [pending.length, tickMs]);

  // Cancel everything on unmount — destructive actions DO NOT fire.
  useEffect(() => () => {
    for (const entry of timersRef.current.values()) clearTimeout(entry.handle);
    timersRef.current.clear();
    if (tickRef.current) clearInterval(tickRef.current);
  }, []);

  const undo = useCallback((id) => {
    const entry = timersRef.current.get(id);
    if (entry) {
      clearTimeout(entry.handle);
      timersRef.current.delete(id);
    }
    setPending((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const schedule = useCallback((fire, { delayMs = 5000, label = 'Action', key = null } = {}) => {
    idCounterRef.current += 1;
    const id = `def_${idCounterRef.current}`;
    const expiresAt = Date.now() + delayMs;

    // De-dup by key (e.g. sitting_id) — a re-schedule replaces the
    // earlier pending entry rather than queuing two cancels.
    setPending((prev) => {
      const filtered = key ? prev.filter((p) => p.key !== key) : prev;
      return [...filtered, { id, label, expiresAt, key }];
    });
    if (key != null) {
      // Cancel any previous timer with the same key. setTimeout returns
      // a numeric id in browsers, so we keep the key alongside the handle
      // in our ref map rather than attaching it to the handle itself.
      for (const [tid, entry] of timersRef.current.entries()) {
        if (entry.key === key) {
          clearTimeout(entry.handle);
          timersRef.current.delete(tid);
        }
      }
    }

    const handle = setTimeout(async () => {
      timersRef.current.delete(id);
      setPending((prev) => prev.filter((p) => p.id !== id));
      try { await fire(); } catch (_e) { /* caller can surface errors via fire() itself */ }
    }, delayMs);
    timersRef.current.set(id, { handle, key });
    return id;
  }, []);

  return { pending, schedule, undo };
}
